/**
 * Wizard — multi-step modal flow with built-in state, navigation, step
 * indicator, footer CTAs, and dirty-close confirmation. Renders as either
 * a fullscreen modal or a slideout based on `mode`.
 *
 * Design principles:
 *   - **Configuration-driven**: declare your steps + per-step CTAs and
 *     navigation gating; the wizard handles the rest.
 *   - **Composable shells**: presentation is delegated to FullscreenModal
 *     or SlideoutMenu so the wizard inherits their layout, animations,
 *     stacking, and a11y for free.
 *   - **Escape hatches**: every default behavior is overridable per step
 *     (custom primary CTA, dynamic labels, hideFooter for terminal views).
 *
 * See {@link WizardStep}, {@link WizardCta}, and {@link WizardCtx} in
 * `./types.ts` for the full configuration surface.
 */

import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ConfirmationDialog } from '@/lib/base/overlays/confirmation-dialog';
import { FullscreenModal } from '@/lib/base/overlays/fullscreen-modal';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import { cx } from '@/utils/cx';

import type { WizardCtx, WizardDiscardConfirmation, WizardStep } from './types';
import { WizardProvider } from './wizard-context';
import { WizardFooter } from './wizard-footer';
import { WizardStepIndicator } from './wizard-step-indicator';

// =============================================================================
// Defaults
// =============================================================================

const DEFAULT_DISCARD_CONFIRMATION: Required<WizardDiscardConfirmation> = {
  title: 'Discard unsaved changes?',
  description:
    'You have unsaved changes that will be lost if you close without saving.',
  confirmLabel: 'Discard',
  cancelLabel: 'Keep editing',
};

// =============================================================================
// Props
// =============================================================================

export interface WizardSlideoutOptions {
  /** Initial width in pixels. */
  initialWidth?: number;
  /** Minimum width in pixels (default 320). */
  minWidth?: number;
  /** Maximum width in pixels (default 800). */
  maxWidth?: number;
  /** Enable drag-to-resize. */
  resizable?: boolean;
}

export interface WizardFullscreenOptions {
  /**
   * Override the max width of the centered content column, in pixels.
   * Applies to the body wrapper (header and footer rows have their own
   * widths). When omitted, the wizard uses a 672px column (Tailwind's
   * `max-w-2xl`) which is comfortable for forms — pass a wider value
   * (e.g. `1024`) for wizards that render tables or other content that
   * benefits from more horizontal space.
   */
  maxContentWidth?: number;
  /**
   * Hide the header close (X) button on mobile (< md). Use together with
   * `showCancelButton` so mobile users still have an exit affordance via the
   * footer — this avoids the X overlaying the step indicator on narrow
   * screens. Default: false (X shown at all sizes).
   */
  hideCloseButtonOnMobile?: boolean;
}

interface WizardPropsBase {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;

  /** Ordered list of steps. Must be non-empty. */
  steps: WizardStep[];

  /**
   * Initial step (by stable id) when the wizard opens. Defaults to the
   * first step. Wizard resets to this step every time `isOpen` flips
   * false → true.
   */
  defaultStepId?: string;

  /**
   * Called when the user advances past the final step's primary CTA.
   * The wizard tracks `isSubmitting` for the duration of the returned
   * promise; throw / reject to keep the user on the current step. After
   * a successful resolve, the consumer typically calls `onOpenChange(false)`
   * (or advances to a hideFooter terminal step via `ctx.next()`).
   */
  onConfirm?: (ctx: WizardCtx) => Promise<void> | void;

  /**
   * Called when the user changes step (next/back/goTo). Useful for
   * analytics or syncing external state.
   */
  onStepChange?: (stepId: string, stepIndex: number) => void;

  /**
   * Whether the wizard has unsaved changes. When `true`, close attempts
   * route through a confirmation dialog (unless `discardConfirmation: false`).
   */
  isDirty?: boolean;
  /**
   * Copy overrides for the discard confirmation dialog. Pass `false` to
   * opt out of dirty-close confirmation entirely.
   */
  discardConfirmation?: WizardDiscardConfirmation | false;

  /**
   * Show a Cancel button in the footer (leftmost) that closes the wizard
   * (routing through the dirty-close confirmation when `isDirty`). Useful as
   * the primary exit affordance when the header close button is hidden — e.g.
   * paired with `fullscreenProps.hideCloseButtonOnMobile`. Default: false.
   */
  showCancelButton?: boolean;

  /** Hide the step indicator in the header. Default: true (shown). */
  showStepIndicator?: boolean;
  /**
   * Allow clicking previously-completed steps in the indicator to jump
   * back to them. Default: true.
   */
  allowRevisitCompletedSteps?: boolean;

  /** Accessible label for the wrapping dialog. */
  dialogAriaLabel?: string;
  /** Test handle — sets `data-instance` on the dialog root. */
  instance?: string;
}

interface WizardFullscreenProps extends WizardPropsBase {
  mode: 'fullscreen';
  /** Fullscreen-mode shell options. */
  fullscreenProps?: WizardFullscreenOptions;
  slideoutProps?: never;
}

interface WizardSlideoutProps extends WizardPropsBase {
  mode: 'slideout';
  /** Slideout-mode shell options. */
  slideoutProps?: WizardSlideoutOptions;
  fullscreenProps?: never;
}

export type WizardProps = WizardFullscreenProps | WizardSlideoutProps;

// =============================================================================
// Helpers
// =============================================================================

function resolveStepIndex(
  steps: WizardStep[],
  idOrIndex: string | number
): number {
  if (typeof idOrIndex === 'number') {
    return idOrIndex;
  }
  return steps.findIndex(s => s.id === idOrIndex);
}

// =============================================================================
// Main component
// =============================================================================

export function Wizard({
  mode,
  isOpen,
  onOpenChange,
  steps,
  defaultStepId,
  onConfirm,
  onStepChange,
  isDirty = false,
  discardConfirmation,
  showCancelButton = false,
  showStepIndicator = true,
  allowRevisitCompletedSteps = true,
  dialogAriaLabel,
  instance,
  slideoutProps,
  fullscreenProps,
}: WizardProps) {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const initialStepIndex = useMemo(() => {
    if (!defaultStepId) {
      return 0;
    }
    const idx = steps.findIndex(s => s.id === defaultStepId);
    return idx >= 0 ? idx : 0;
  }, [defaultStepId, steps]);

  const [currentStepIndex, setCurrentStepIndex] = useState(initialStepIndex);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Reset to the initial step ONLY on isOpen false→true transitions, and
  // clear the discard-confirm dialog whenever the wizard closes. The
  // prevIsOpenRef guards against `initialStepIndex` changing reactively
  // while the wizard is already open (e.g. a consumer's `defaultStepId`
  // updating from a refetched query) — that previously stomped the user's
  // current step. We keep `initialStepIndex` in the dep array so the ref
  // stays in sync with the latest open state without needing a lint
  // suppression.
  const prevIsOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;
    if (isOpen) {
      if (!wasOpen) {
        setCurrentStepIndex(initialStepIndex);
      }
    } else {
      setShowDiscardConfirm(false);
    }
  }, [isOpen, initialStepIndex]);

  const totalSteps = steps.length;
  const currentStep = steps[currentStepIndex];

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  const goTo = useCallback(
    (idOrIndex: string | number) => {
      const idx = resolveStepIndex(steps, idOrIndex);
      if (idx < 0 || idx >= steps.length) {
        return;
      }
      setCurrentStepIndex(idx);
      onStepChange?.(steps[idx].id, idx);
    },
    [steps, onStepChange]
  );

  const next = useCallback(() => {
    setCurrentStepIndex(prev => {
      const nextIndex = Math.min(prev + 1, steps.length - 1);
      if (nextIndex !== prev) {
        onStepChange?.(steps[nextIndex].id, nextIndex);
      }
      return nextIndex;
    });
  }, [steps, onStepChange]);

  const back = useCallback(() => {
    setCurrentStepIndex(prev => {
      const nextIndex = Math.max(prev - 1, 0);
      if (nextIndex !== prev) {
        onStepChange?.(steps[nextIndex].id, nextIndex);
      }
      return nextIndex;
    });
  }, [steps, onStepChange]);

  // ---------------------------------------------------------------------------
  // Dirty-close handling
  // ---------------------------------------------------------------------------

  const dirtyConfirmEnabled = discardConfirmation !== false;
  const discardCopy = useMemo<Required<WizardDiscardConfirmation>>(
    () => ({
      ...DEFAULT_DISCARD_CONFIRMATION,
      ...(discardConfirmation && typeof discardConfirmation === 'object'
        ? discardConfirmation
        : {}),
    }),
    [discardConfirmation]
  );

  const close = useCallback(() => {
    if (isSubmitting) {
      // Never close out from under an in-flight submission.
      return;
    }
    if (isDirty && dirtyConfirmEnabled) {
      setShowDiscardConfirm(true);
      return;
    }
    onOpenChange(false);
  }, [isSubmitting, isDirty, dirtyConfirmEnabled, onOpenChange]);

  const handleConfirmDiscard = useCallback(() => {
    setShowDiscardConfirm(false);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleCancelDiscard = useCallback(() => {
    setShowDiscardConfirm(false);
  }, []);

  // The modal shell calls onOpenChange(false) on its own close affordances
  // (close button, click outside if enabled, Escape if enabled). Route
  // those through the dirty check.
  const handleShellOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        onOpenChange(true);
        return;
      }
      close();
    },
    [close, onOpenChange]
  );

  // ---------------------------------------------------------------------------
  // CTA gating / handlers
  // ---------------------------------------------------------------------------

  const canAdvance = currentStep?.canAdvance?.() ?? true;

  const onNextRequest = useCallback(async () => {
    if (!currentStep) {
      return;
    }
    if (!canAdvance) {
      return;
    }
    if (currentStep.onBeforeNext) {
      setIsSubmitting(true);
      try {
        await currentStep.onBeforeNext();
      } catch {
        // Step opted to block — stay put. Surfacing the error is the
        // step's responsibility (display in its body).
        return;
      } finally {
        setIsSubmitting(false);
      }
    }
    next();
  }, [currentStep, canAdvance, next]);

  // ctx must be available to onConfirmRequest; build it below first.
  const ctx = useMemo<WizardCtx>(
    () => ({
      currentStepIndex,
      currentStepId: currentStep?.id ?? '',
      totalSteps,
      isFirst: currentStepIndex === 0,
      isLast: currentStepIndex === totalSteps - 1,
      isSubmitting,
      next,
      back,
      goTo,
      close,
    }),
    [
      currentStepIndex,
      currentStep,
      totalSteps,
      isSubmitting,
      next,
      back,
      goTo,
      close,
    ]
  );

  const onConfirmRequest = useCallback(async () => {
    if (!onConfirm) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onConfirm(ctx);
    } catch {
      // Consumer surfaces errors via their step body; wizard just keeps
      // the user on the current step.
      return;
    } finally {
      setIsSubmitting(false);
    }
  }, [onConfirm, ctx]);

  // Wraps any CTA's custom `onClick` so the wizard tracks submission
  // state for the duration of the returned promise (matches the
  // documented contract on `WizardCta.onClick`) and swallows rejections
  // the same way `onConfirm` / `onBeforeNext` do — the step body owns
  // surfacing errors.
  const onCtaClickRequest = useCallback(
    async (onClick: (ctx: WizardCtx) => Promise<void> | void) => {
      setIsSubmitting(true);
      try {
        await onClick(ctx);
      } catch {
        return;
      } finally {
        setIsSubmitting(false);
      }
    },
    [ctx]
  );

  // ---------------------------------------------------------------------------
  // Render — wrap the layout-managed body in either modal shell
  // ---------------------------------------------------------------------------

  // Compose canAdvance into the primary CTA's disabled state so consumers
  // don't have to wire it manually. Per-step `primaryCta.isDisabled` still
  // wins if explicitly set to true.
  const effectivePrimaryDisabled = !canAdvance;

  const stepIndicatorSteps = useMemo(
    () => steps.map(s => ({ id: s.id, label: s.label })),
    [steps]
  );

  const handleStepIndicatorClick = useCallback(
    (stepIndex: number) => {
      if (!allowRevisitCompletedSteps) {
        return;
      }
      // Never navigate away while a step's onBeforeNext/onConfirm poll is in
      // flight. That step hosts the poll's only escape hatch (e.g.
      // WizardPollingBody's "Stop waiting"); jumping to a completed step would
      // strip the user's sole exit while every footer CTA and the header close
      // button are disabled by isSubmitting, and the completing poll's next()
      // would then advance from the wrong step. The indicator also drops its
      // click affordance below, so this is a defensive backstop.
      if (isSubmitting) {
        return;
      }
      if (stepIndex < currentStepIndex) {
        goTo(stepIndex);
      }
    },
    [allowRevisitCompletedSteps, isSubmitting, currentStepIndex, goTo]
  );

  const headerContent = useMemo<ReactNode>(() => {
    if (!showStepIndicator) {
      return null;
    }
    return (
      <div className='mx-auto w-full max-w-lg'>
        <WizardStepIndicator
          steps={stepIndicatorSteps}
          currentStepIndex={currentStepIndex}
          // Drop the click affordance while a poll holds isSubmitting so a
          // completed step can't be navigated to mid-poll (which would hide
          // the active step's escape hatch). Completed steps render as plain,
          // non-interactive circles for the duration.
          onStepClick={
            allowRevisitCompletedSteps && !isSubmitting
              ? handleStepIndicatorClick
              : undefined
          }
        />
      </div>
    );
  }, [
    showStepIndicator,
    stepIndicatorSteps,
    currentStepIndex,
    allowRevisitCompletedSteps,
    isSubmitting,
    handleStepIndicatorClick,
  ]);

  // Layout config is memoized per-step; the render() call itself is NOT
  // memoized so the step body always reflects the latest state. A previous
  // version wrapped the entire JSX (including render()) in useMemo, which
  // prevented re-renders when step state changed via refs/callbacks.
  const bodyLayout = useMemo(() => {
    if (!currentStep) {
      return null;
    }
    const effectiveMaxContentWidth =
      currentStep.maxContentWidth ??
      (mode === 'fullscreen' ? fullscreenProps?.maxContentWidth : undefined);
    const maxWidthOverride = effectiveMaxContentWidth
      ? `${effectiveMaxContentWidth}px`
      : undefined;
    const fillHeight = currentStep.fillHeight === true;
    const outerClassName = fillHeight
      ? 'flex w-full flex-1 flex-col min-h-0 px-4 pt-6 md:px-6'
      : 'w-full px-4 py-8 md:px-6';
    const innerClassName = cx(
      'mx-auto w-full',
      fillHeight && 'flex flex-1 flex-col min-h-0',
      !maxWidthOverride && 'max-w-2xl'
    );
    return { outerClassName, innerClassName, maxWidthOverride, fillHeight };
  }, [currentStep, mode, fullscreenProps?.maxContentWidth]);

  const bodyContent =
    !currentStep || !bodyLayout ? null : (
      <div className={bodyLayout.outerClassName}>
        <div
          className={bodyLayout.innerClassName}
          style={
            bodyLayout.maxWidthOverride
              ? { maxWidth: bodyLayout.maxWidthOverride }
              : undefined
          }
        >
          {(currentStep.title || currentStep.subtitle) && (
            <div className='mb-8 text-center'>
              {currentStep.title && (
                <h2 className='text-heading-2 font-semibold text-[var(--color-text-primary)]'>
                  {currentStep.title}
                </h2>
              )}
              {currentStep.subtitle && (
                <p className='text-body mt-1 text-[var(--color-text-tertiary)]'>
                  {currentStep.subtitle}
                </p>
              )}
            </div>
          )}
          {currentStep.render()}
        </div>
      </div>
    );

  // Apply canAdvance to the step's primary CTA at render time. We do this
  // here (vs inside WizardFooter) to keep the footer file pure / dumb.
  const effectiveStep = useMemo<WizardStep | undefined>(() => {
    if (!currentStep) {
      return undefined;
    }
    // If canAdvance is false, force-disable the primary CTA by injecting
    // isDisabled=true into the auto/resolved primary CTA. Don't override
    // explicit consumer-provided isDisabled callbacks.
    if (!effectivePrimaryDisabled) {
      return currentStep;
    }
    const original = currentStep.primaryCta;
    const merged =
      original && original !== 'auto'
        ? { ...original, isDisabled: true }
        : { label: ctx.isLast ? 'Confirm' : 'Next', isDisabled: true };
    return { ...currentStep, primaryCta: merged };
  }, [currentStep, effectivePrimaryDisabled, ctx.isLast]);

  // Align the footer CTAs with the step's content column (same width as
  // bodyContent), so a step with a custom maxContentWidth doesn't leave the
  // primary CTA sitting inside the content's right edge.
  const footerMaxWidth =
    effectiveStep?.maxContentWidth ??
    (mode === 'fullscreen' ? fullscreenProps?.maxContentWidth : undefined);

  // Not memoized, same reasoning as bodyContent above: a step's primary CTA
  // label is often a function of external mutable state (e.g. a dispatched
  // job's poll status) read at call time via a ref, not via ctx. Memoizing
  // this on [effectiveStep, ctx, ...] — none of which change when that
  // external state changes — froze the CTA on whatever label it had when
  // isSubmitting first flipped true, so a later status change (e.g.
  // 'pending' -> 'running') never reached the button.
  const footerContent: ReactNode =
    !effectiveStep || effectiveStep.hideFooter ? null : (
      <WizardFooter
        step={effectiveStep}
        ctx={ctx}
        maxWidth={footerMaxWidth}
        showCancel={showCancelButton}
        onNextRequest={onNextRequest}
        onConfirmRequest={onConfirmRequest}
        onCtaClickRequest={onCtaClickRequest}
      />
    );

  // ---------------------------------------------------------------------------
  // Shell render
  // ---------------------------------------------------------------------------

  const shellChildren = (
    <WizardProvider value={ctx}>{bodyContent}</WizardProvider>
  );

  const dialog =
    mode === 'fullscreen' ? (
      <FullscreenModal
        isOpen={isOpen}
        onOpenChange={handleShellOpenChange}
        header={headerContent}
        footer={footerContent}
        dialogAriaLabel={dialogAriaLabel}
        instance={instance}
        // The wizard centers its own body column (see `bodyLayout`) so it can
        // honor per-step `maxContentWidth` overrides. Opt out of the modal's
        // own centering rather than nesting two width constraints, which would
        // clamp any step wider than the modal default.
        maxContentWidth='none'
        closeButtonClassName={
          fullscreenProps?.hideCloseButtonOnMobile
            ? 'hidden md:flex'
            : undefined
        }
        // Reserve the scrollbar gutter symmetrically so the centered body column
        // stays centered in the viewport whether or not a scrollbar is present.
        // Without this the body centers within (viewport − scrollbar) while the
        // pinned footer (outside the scroll region) centers within the full
        // viewport, so the footer CTAs sit slightly off from the content column.
        contentClassName='[scrollbar-gutter:stable_both-edges]'
      >
        {shellChildren}
      </FullscreenModal>
    ) : (
      <SlideoutMenu
        isOpen={isOpen}
        onOpenChange={handleShellOpenChange}
        header={headerContent}
        footer={footerContent}
        dialogAriaLabel={dialogAriaLabel}
        instance={instance}
        resizable={slideoutProps?.resizable}
        initialWidth={slideoutProps?.initialWidth}
        minWidth={slideoutProps?.minWidth}
        maxWidth={slideoutProps?.maxWidth}
      >
        {shellChildren}
      </SlideoutMenu>
    );

  return (
    <>
      {dialog}
      <ConfirmationDialog
        isOpen={showDiscardConfirm}
        title={discardCopy.title}
        description={discardCopy.description}
        variant='warning'
        confirmLabel={discardCopy.confirmLabel}
        cancelLabel={discardCopy.cancelLabel}
        onConfirm={handleConfirmDiscard}
        onCancel={handleCancelDiscard}
      />
    </>
  );
}
